/**
 * Canva Connect — design assets (OAuth + list designs + PNG export).
 * Client secret and token exchange stay in Cloud Functions.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

export const CANVA_INTEGRATION_PHASE = 1 as const;

export const CANVA_CUSTOM_SIZE_URL = 'https://www.canva.com/create/custom-size/';

export const CANVA_CONNECT_DOCS_URL = 'https://www.canva.dev/docs/connect/';

export function parseEmailContainerWidthToPx(containerWidth?: string): number {
  if (!containerWidth) return 600;
  const m = String(containerWidth).trim().match(/^(\d+)/);
  if (!m) return 600;
  const n = parseInt(m[1], 10);
  if (Number.isNaN(n)) return 600;
  return Math.min(Math.max(n, 320), 1200);
}

export function isCanvaConnectClientConfigured(): boolean {
  const id = process.env.REACT_APP_CANVA_CONNECT_CLIENT_ID;
  return typeof id === 'string' && id.trim().length > 0;
}

export type CanvaConnectServerStatus = {
  integrationConfigured: boolean;
  connected: boolean;
  phase: number;
  message: string;
};

export async function fetchCanvaConnectServerStatus(): Promise<CanvaConnectServerStatus | null> {
  try {
    const fn = httpsCallable(functions, 'getCanvaConnectStatus');
    const res = await fn({});
    const d = res.data as Record<string, unknown>;
    return {
      integrationConfigured: Boolean(d.integrationConfigured),
      connected: Boolean(d.connected),
      phase: typeof d.phase === 'number' ? d.phase : CANVA_INTEGRATION_PHASE,
      message: typeof d.message === 'string' ? d.message : '',
    };
  } catch {
    return null;
  }
}

export async function startCanvaOAuth(returnPath: string): Promise<{ authorizationUrl: string } | null> {
  try {
    const fn = httpsCallable(functions, 'startCanvaOAuth');
    const res = await fn({ returnPath });
    const d = res.data as { authorizationUrl?: string };
    if (typeof d.authorizationUrl === 'string') return { authorizationUrl: d.authorizationUrl };
    return null;
  } catch {
    return null;
  }
}

export type CanvaDesignListItem = {
  id: string;
  title: string;
  thumbnailUrl: string | null;
  updatedAt?: number;
};

export async function listCanvaDesigns(options?: {
  limit?: number;
  continuation?: string;
}): Promise<{ items: CanvaDesignListItem[]; continuation: string | null } | null> {
  try {
    const fn = httpsCallable(functions, 'listCanvaDesigns');
    const res = await fn({
      limit: options?.limit,
      continuation: options?.continuation,
    });
    const d = res.data as { items?: CanvaDesignListItem[]; continuation?: string | null };
    return {
      items: Array.isArray(d.items) ? d.items : [],
      continuation: d.continuation ?? null,
    };
  } catch {
    return null;
  }
}

export async function exportCanvaDesignPng(designId: string, width?: number): Promise<{ downloadUrl: string } | null> {
  try {
    const fn = httpsCallable(functions, 'exportCanvaDesignPng');
    const res = await fn({ designId, width });
    const d = res.data as { downloadUrl?: string };
    if (typeof d.downloadUrl === 'string') return { downloadUrl: d.downloadUrl };
    return null;
  } catch {
    return null;
  }
}

export async function disconnectCanva(): Promise<boolean> {
  try {
    const fn = httpsCallable(functions, 'disconnectCanva');
    await fn({});
    return true;
  } catch {
    return false;
  }
}
